import { ThemeProvider, theme } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import { ClientCultureProvider } from '@ori/culture';
import { Team, TestIdProvider } from '@ori/testid-generator';
import { Suspense, type FC } from 'react';

import { BasicSkeleton } from './components';
import { APP_NAME } from './constants';
import { ApiProvider, ContentsContainer, ErrorBoundary, I18nContainer, RegisterStatusContainer } from './containers';
import {
  ConfigurationProvider,
  FormErrorProvider,
  NotificationProvider,
  StepperProvider,
  VirtualPageAnalyticsProvider,
} from './contexts';
import { LandingPage } from './pages';
import type { ClientAppConfig } from './types/ClientAppConfig';

export type AppProps = { configuration: ClientAppConfig };

export const App: FC<AppProps> = ({ configuration }) => {
  const { formattedToken } = useToken();

  if (!formattedToken) {
    return null;
  }
  return (
    <ErrorBoundary>
      <ConfigurationProvider value={configuration}>
        <TestIdProvider
          team={Team.Acquisition}
          project={APP_NAME}
        >
          <Suspense fallback={<BasicSkeleton />}>
            <ThemeProvider
              enableCacheProvider
              theme={theme}
              cacheKey={APP_NAME}
            >
              <I18nContainer>
                <ApiProvider>
                  <VirtualPageAnalyticsProvider>
                    <RegisterStatusContainer>
                      <StepperProvider>
                        <NotificationProvider>
                          <FormErrorProvider>
                            <ClientCultureProvider
                              tenant={configuration.tenant}
                              language={configuration.currentLanguage}
                              options={{ swr: { suspense: true } }}
                            >
                              <ContentsContainer>
                                <LandingPage />
                              </ContentsContainer>
                            </ClientCultureProvider>
                          </FormErrorProvider>
                        </NotificationProvider>
                      </StepperProvider>
                    </RegisterStatusContainer>
                  </VirtualPageAnalyticsProvider>
                </ApiProvider>
              </I18nContainer>
            </ThemeProvider>
          </Suspense>
        </TestIdProvider>
      </ConfigurationProvider>
    </ErrorBoundary>
  );
};
